import { Button, Stack, Typography } from '@mui/material';
import { SnackbarKey, useSnackbar } from 'notistack';
import { useState } from 'react';

import SnackbarCloseButton from 'modules/common/components/SnackbarCloseButton';

interface UndoWaiverProps {
    /** The snackbar this sits in, so either button can dismiss its own toast. */
    readonly snackbarKey: SnackbarKey;
    /** Reverses the waiver. Owned by the caller, which knows which account was waived. */
    readonly onUndo: () => Promise<boolean>;
}

/**
 * The action slot on the "marked as unavailable" toast: _Undo_, then a dismiss.
 *
 * The dismiss is rendered here rather than inherited, because `CustomAlert` has a single action
 * slot — passing an `action` at all replaces the provider's close. It is the provider's own
 * `SnackbarCloseButton`, so the two cannot drift apart.
 *
 * Undo stays on screen until its write lands, disabled meanwhile. Closing on click would take the
 * double-click off the table too, but it also takes away the only thing naming the account: a
 * failed undo raises `SAVE_FAILED_MESSAGE`, which says "please try again" and nothing about what
 * to try again on. Held open, the retry is where the member is already looking.
 */
export default function UndoWaiver({ snackbarKey, onUndo }: Readonly<UndoWaiverProps>) {
    const { closeSnackbar } = useSnackbar();
    const [isUndoing, setIsUndoing] = useState(false);

    const handleUndo = async () => {
        setIsUndoing(true);
        if (await onUndo()) {
            closeSnackbar(snackbarKey);
            return;
        }
        // The waiver still stands, so the affordance that reverses it has to as well.
        setIsUndoing(false);
    };

    return (
        <Stack direction="row" alignItems="center" spacing={2}>
            <Button
                variant="text"
                color="inherit"
                aria-label="Undo marking unavailable"
                disabled={isUndoing}
                onClick={handleUndo}>
                <Typography variant="overline">Undo</Typography>
            </Button>
            {/* The provider's own default, rendered back rather than re-implemented: passing an
                `action` replaces it. */}
            <SnackbarCloseButton snackbarKey={snackbarKey} />
        </Stack>
    );
}
